import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';
import MenuItemTypeSelect from 'components/MenuItemTypeSelect';
import IconSelect from 'components/IconSelect';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import {
  MenuItemType,
  AddMenuItemDialogMutation,
} from '__generated__/AddMenuItemDialogMutation.graphql';
import { AddMenuItemDialog_menu$key } from '__generated__/AddMenuItemDialog_menu.graphql';

type FormFields = {
  name: string;
  type: MenuItemType;
  url: string;
  icon: string;
};

type Props = {
  menu: AddMenuItemDialog_menu$key;
  connectionId: string;
};

const initial = {
  name: '',
  type: 'PACKAGES' as MenuItemType,
  url: '',
  icon: '',
};

const AddMenuItemDialog: FC<Props> = ({ connectionId, menu }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AddMenuItemDialog_menu on Menu {
        id
      }
    `,
    menu
  );

  const [add, adding] = useMutation<AddMenuItemDialogMutation>(graphql`
    mutation AddMenuItemDialogMutation(
      $id: ID!
      $input: CreateMenuItemInput!
      $connections: [ID!]!
    ) {
      addMenuItemToMenu(id: $id, input: $input) {
        node
          @appendNode(connections: $connections, edgeTypeName: "MenuItemEdge") {
          ...MenuItemsListRow_menuItem
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          id,
          input,
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [add, close, connectionId, errorHandler, id]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas meniu punktas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują meniu punktą į egizistuojantį meniu.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus fullWidth name="name" label="Pavadinimas" />
            <MenuItemTypeSelect name="type" label="Tipas" />
            <TextField fullWidth name="url" label="Nuoroda" />
            <IconSelect name="icon" label="Ikona" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddMenuItemDialog;
